// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.HighchartOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import ChartOptionsBuilder from '../Izenda.ChartOptionsBuilder';
import {highcharts as highcharts} from 'wrappers/Izenda.Highcharts';
import {
  get,
  omitBy,
  size,
  isNil,
  isArray,
  forEach,
  filter,
  map,
  mapValues,
  escape,
  set,
  getOr,
  isString,
  isObject,
  isEmpty,
  merge,
  mergeAll,
  findIndex
} from 'lodash/fp';

import { getPointName, getTooltipFormatter, getColorPointBySeries } from './Izenda.HighchartUtils';
import { UNDEFINED_VALUE } from 'logic/data/Izenda.UIConstants';
import { MappingValues } from 'logic/data/Izenda.ChartDefaultConfig';
import { exportClass } from 'share/Izenda.ServiceProvider';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import CHART_TYPE from './Izenda.VisualTypeMapping';

const { LABEL_ROTATION_VALUES } = MappingValues;
const SERIES_ITEM_MAX_HEIGHT = 200;
const SERIES_ITEM_PADDING = 20;
const SERIES_ITEM_TITLE_SIZE = '12px';
const SERIES_ITEM_TITLE_ALIGN = 'bottom';
const CHART_SPACING_TOP = 70;
const CHART_SPACING_RIGHT = 70;

@exportClass('HighchartOptionsBuilder')
export default class HighchartOptionsBuilder extends ChartOptionsBuilder {
  constructor(...baseArgs) {
    super(...baseArgs);
  }

  get DefaultColors() {
    return highcharts.getOptions().colors;
  }

  build() {
    const { chartData, chartOptions, fieldOptions, visualType, chartContainer } = this;

    // Build common highchart options
    const commonOptions = this.buildCommonHighchartOptions(chartContainer, visualType, chartData, {
      chartOptions,
      fieldOptions
    });

    // Adjust chart spacing on particular label orientation
    this.adjustChartSpacings(commonOptions);

    // Depends on each chart type, the chart data is convert into Highchart options that are required to
    // render particular chart properly.
    const optionsByType = this.buildOptionsByType(visualType, commonOptions, chartData);

    const { xAxis, yAxis, izendaSeriesConfig, ...restCommonOpts } = commonOptions;

    // Merge and let optionsByType override the commonOptions.
    // This is important step to ensure the final options will take all of optgionsByType.
    // The commonOptions acts as the default options.
    const highchartOptions = escapeChartTitle(
      mergeAll([{ izendaSeriesConfig: izendaSeriesConfig }, restCommonOpts], optionsByType)
    );

    // add color to each point
    size(highchartOptions.series) > 0 &&
      getColorPointBySeries(highchartOptions.series, highchartOptions, izendaSeriesConfig);

    // omit all properties that have null or undefined value of highchart options.
    // It ensures the Highchart library doesn't throw error when processing null value.
    return omitBy(isNil, highchartOptions);
  }

  /**
   * Build Highchart variable options for each chart type
   * @param {string} visualType Highchart chart type
   * @param {Object} highchartOptions Highchart options
   * @param {Object} chartData Chart data
   */
  buildOptionsByType(visualType: string, highchartOptions: Object, chartData: Object): Object {
    throw new Error(`The buildOptionsByType() method must be implemented for ${visualType} chart before using`);
  }

  /**
   * Build common Highchart option structure
   * @param {DOMElement} container DOM element of chart
   * @param {string} visualType Highchart chart type
   * @param {Object} chartData Chart data
   * @param {Object} options Options
   */
  buildCommonHighchartOptions(container, visualType, chartData, { chartOptions, fieldOptions }) {
    ensure(chartOptions, 'chartOptions must be set');

    const onClickOnPoint = this.onClickOnPoint;
    const onClickOnChart = this.onClickOnChart;
    const getClassList = this.getClassList;

    const { commonOptions, optionByType, commonActions, xAxis, yAxis, commonXYAxis, sort, colors } = chartOptions;

    return mergeAll([optionByType, commonOptions], {
      izendaSeriesConfig: fieldOptions,
      credits: {
        // disable credits text
        enabled: false
      },
      chart: {
        label: {
          enabled: false
        },
        type: visualType,
        events: {
          click: function() {
            const point = getOr({}, 'hoverPoints[0]', this);
            return onClickOnChart(point, commonActions);
          }
        }
      },
      title: { text: null },
      tooltip: {
        backgroundColor: 'rgba(255,255,255,0)',
        borderWidth: 0,
        borderRadius: 0,
        shadow: false,
        shared: true,
        useHTML: true,
        formatter: function() {
          // disable title of chart - always
          const sortValue = get('valueXAxisForLinear', this.sort);
          const needReverseTooltip = sortValue === 'DESC' && (visualType === 'column' || visualType === 'bar');
          return getTooltipFormatter.call(this, needReverseTooltip);
        }
      },
      plotOptions: {
        series: {
          cursor: 'pointer',
          label: {
            enabled: false
          },
          point: {
            events: {
              click: function (e) {
                const fromPoint = findIndex(c => c === 'highcharts-point', getClassList(e.srcElement)) !== -1;
                if (fromPoint)
                  onClickOnPoint(this, commonActions);
              }
            }
          },
          turboThreshold: Number.MAX_VALUE,
          connectNulls: true,
          marker: {
            enabled: true,
            states: {
              hover: {
                fillColor: function() {
                  return true;
                }
              }
            }
          }
        }
      },
      data: chartData && chartData.parsedData,
      commonActions: { ...commonActions, chartContainer: container },
      xAxis: xAxis,
      yAxis: yAxis,
      commonXYAxis: commonXYAxis,
      sort: sort,
      colors: colors
    });
  }

  getClassList(svgElement) {
    if (svgElement.classList)
      return svgElement.classList;
    const className = svgElement.getAttribute('class') || '';
    return className.split(' ');
  }

  /**
   * Adjust chart spacing according to labels orientation
   * @param {Object} highChartOptions Highchart options
   */
  adjustChartSpacings(highChartOptions) {
    if (
      (highChartOptions.chart.type === CHART_TYPE.Combination ||
        highChartOptions.chart.type === CHART_TYPE.Line ||
        highChartOptions.chart.type === CHART_TYPE.Column ||
        highChartOptions.chart.type === CHART_TYPE.Area) &&
      highChartOptions.commonXYAxis.yAxis.labels &&
      (highChartOptions.commonXYAxis.yAxis.labels.rotation === LABEL_ROTATION_VALUES.izClockwise ||
        highChartOptions.commonXYAxis.yAxis.labels.rotation === LABEL_ROTATION_VALUES.izCounterclockwise ||
        highChartOptions.commonXYAxis.yAxis.labels.rotation === LABEL_ROTATION_VALUES.izTextdown ||
        highChartOptions.commonXYAxis.yAxis.labels.rotation === LABEL_ROTATION_VALUES.izTextup)
    ) {
      highChartOptions.chart.spacingTop = CHART_SPACING_TOP;
    }

    // if chart type is combination/line/area,
    //  ==> set spacingRight when xAxis is clockwise
    if (
      (highChartOptions.chart.type === CHART_TYPE.Combination ||
        highChartOptions.chart.type === CHART_TYPE.Line ||
        highChartOptions.chart.type === CHART_TYPE.Area) &&
      highChartOptions.commonXYAxis.xAxis.labels &&
      highChartOptions.commonXYAxis.xAxis.labels.rotation === LABEL_ROTATION_VALUES.izClockwise
    ) {
      highChartOptions.chart.spacingRight = CHART_SPACING_RIGHT;
    }

    // if chart type is bar, set spacingTop when xAxis is clockwise and spacingRight if yAxis is clockwise
    if (highChartOptions.chart.type === CHART_TYPE.Bar && highChartOptions.commonXYAxis.xAxis.labels) {
      if (highChartOptions.commonXYAxis.xAxis.labels.rotation === LABEL_ROTATION_VALUES.izClockwise) {
        highChartOptions.chart.spacingTop = CHART_SPACING_TOP;
      }
      if (highChartOptions.commonXYAxis.yAxis.labels.rotation === LABEL_ROTATION_VALUES.izClockwise) {
        highChartOptions.chart.spacingRight = CHART_SPACING_RIGHT;
      }
    }
  }

  /**
   * Handle click event on chart
   * @param {Object} point Data point at mouse pointer
   * @param {Object} commonActions Common defined actions on chart
   */
  onClickOnChart(point, commonActions) {
    if (isEmpty(point)) {
      return;
    }

    const serieOpt = get('series.userOptions', point);
    !serieOpt.isRegressionLine &&
      commonActions.onDrilldown &&
      commonActions.onDrilldown({
        field: serieOpt.xAxisField,
        value: getPointName(point),
        record: get('options.record', point)
      }, null);
  }

  /**
   * Handle click event on data point
   * @param {Object} point Data point
   * @param {Object} commonActions Common defined actions on chart
   */
  onClickOnPoint(point, commonActions) {
    const serieOpt = getOr({}, 'series.userOptions', point);

    // Do not drilldown on regression line
    if (serieOpt.isRegressionLine) {
      return;
    }
    const pointValue = getPointName(point);
    const separatorField = get('separatorField', serieOpt);
    const record = get('record', point);
    const originalValue = ReportPartUtils.getOriginalValue(separatorField, record);

    commonActions.onDrilldown &&
      commonActions.onDrilldown(
        { field: serieOpt.xAxisField || serieOpt.nameField, value: pointValue, record: get('options.record', point) },
        {
          field: serieOpt.separatorField,
          value: serieOpt.separatorValue === UNDEFINED_VALUE ? '' : originalValue
        }
      );
  }
}

/**
 * Escape text value in chart options
 * @param {Object} param0 Chart options
 */
function escapeChartTitle({ xAxis, yAxis, izendaOptions, ...restOpts }) {
  if (!isNil(xAxis) && !isNil(xAxis.title)) {
    xAxis.title.text = escape(xAxis.title.text);
  }

  if (isArray(yAxis)) {
    forEach(obj => {
      if (!isNil(obj.title)) {
        obj.title.text = escape(obj.title.text);
      }
    }, yAxis);
  } else if (!isNil(yAxis) && !isNil(yAxis.title)) {
    yAxis.title.text = escape(yAxis.title.text);
  }

  if (!isNil(izendaOptions)) {
    izendaOptions.dataUnit = escape(izendaOptions.dataUnit);
  }

  return {
    xAxis,
    yAxis,
    izendaOptions,
    ...restOpts
  };
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/highcharts/Izenda.HighchartOptionsBuilder.js